import {useStore} from 'zustand';
import {useStoresContext} from './useStoresContext';
import {useDashboardServiceRequestsTableStore} from '../store/useDashboardRequestsTableStore';
import {
  GetDashboardServiceRequestsQueryParams,
  useGetDashboardServiceRequestsQuery,
} from '../queries/dashboard-service-requests.query';
import {GetServiceRequestPeriod} from '../../../../../shared/types/serviceRequest.types';
import {useMemo} from 'react';
import {SRDetails} from '../../../../../global/api';
import {useDebounce} from '../../../../../hooks/useDebounce';

type SRDetailsWithPlannedDate = SRDetails & {plannedDate?: string | null};

export const useDashboardServiceRequestsTableData = (serviceType: string) => {
  const {requestsPeriodFiltersStore, serviceRequestsArchiveFilterStore, dashboardServiceRequestsFiltersStore} =
    useStoresContext(serviceType);
  const {period, customPeriodStart, customPeriodEnd} = useStore(requestsPeriodFiltersStore);
  const archived = useStore(serviceRequestsArchiveFilterStore, (s) => s.archived);
  const {searchBy, searchValue, filters} = useStore(dashboardServiceRequestsFiltersStore);
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const {page, size, order} = useDashboardServiceRequestsTableStore();

  const periodFilters: Pick<GetDashboardServiceRequestsQueryParams, 'from' | 'period' | 'to'> = useMemo(() => {
    const requestFilters: Pick<GetDashboardServiceRequestsQueryParams, 'from' | 'period' | 'to'> = {};
    if (customPeriodStart && customPeriodEnd) {
      requestFilters.from = customPeriodStart;
      requestFilters.to = customPeriodEnd;
    } else {
      requestFilters.period = period as GetServiceRequestPeriod;
    }

    return requestFilters;
  }, [period, customPeriodStart, customPeriodEnd]);

  const requestFilters = useMemo<Partial<GetDashboardServiceRequestsQueryParams>>(() => {
    const output: Partial<GetDashboardServiceRequestsQueryParams> = {};
    const normalizedSearchValue = debouncedSearchValue.trim();

    if (normalizedSearchValue) {
      if (searchBy === 'id') {
        const id = Number(normalizedSearchValue);
        if (Number.isFinite(id)) output.id = id;
      }
      if (searchBy === 'extNumber') {
        output.extNumber = normalizedSearchValue;
      }
      if (searchBy === 'deviceNumberNew') {
        output.deviceNumberNew = normalizedSearchValue;
      }
    }

    for (const [key, value] of filters) {
      if (!value?.trim()) continue;
      switch (key) {
        case 'contractNumber': {
          output.contractNumber = value;
          break;
        }
        case 'taskLocationCode':
          output.contractLocation = value;
          break;
        case 'workerUid':
          output.workerId = value;
          break;
        case 'status':
          output.status = value;
          break;
        case 'fullAddress':
          output.fullAddress = value;
          break;
        default:
          break;
      }
    }

    return output;
  }, [searchBy, debouncedSearchValue, filters]);

  const {data, loading} = useGetDashboardServiceRequestsQuery({
    page,
    size,
    order: order(),
    ...periodFilters,
    ...requestFilters,
    serviceType,
    isArchive: archived ? true : undefined,
  });

  const sortedData = useMemo(() => {
    if (!data?.content) return data;

    const getPlannedTime = (request: SRDetailsWithPlannedDate) => {
      if (!request?.plannedDate) return Number.MAX_SAFE_INTEGER;
      const time = new Date(request.plannedDate).getTime();
      return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
    };

    return {
      ...data,
      content: data.content.map((contract) => ({
        ...contract,
        serviceRequests: contract.serviceRequests
          ? [...contract.serviceRequests].sort(
              (a, b) => getPlannedTime(a as SRDetailsWithPlannedDate) - getPlannedTime(b as SRDetailsWithPlannedDate),
            )
          : contract.serviceRequests,
      })),
    };
  }, [data]);

  return {
    data: sortedData,
    loading,
  };
};
